import React, { useState, useEffect } from 'react';
import { styles } from '../../../utils/styles';
import {  MultiChoiceIcon, OverFlowIcon } from 'assests/GmsIcons';
import { useEditor } from '../../../contexts/EditorContext';
import { useComponentRegistry } from '../../../contexts/ComponentRegistryContext';
import TextFieldConfig from './TextFieldConfig';
import IntegerFieldConfig from './IntegerFieldConfig';
import DecimalFieldConfig from './DecimalFieldConfig';
import FractionFieldConfig from './FractionFieldConfig';
import MultiChoiceFieldConfig from './MultiChoiceFieldConfig';
import RangeFieldConfig from './RangeFieldConfig';
import { Container as ContainerType } from '../../../types/canvas.types';
import BooleanFieldConfig from './BooleanFieldConfig';
import ConnectorConfig from './ConnectorFieldConfig';

interface FieldConfigPanelProps {
  id?: string;
  fieldId: string;
  fieldType?: string;
  parentId?: string;
  config?: any;
  onConfigChange?: (config: any) => void;
}

export const FieldConfigPanel: React.FC<FieldConfigPanelProps> = ({  fieldId,  fieldType = 'text', parentId, config, onConfigChange }) => {
  const { containers, editorState } = useEditor();
  const { getComponentDefinition } = useComponentRegistry();
  
  // Get field definition - getComponentDefinition handles null registry internally
  const fieldDef = getComponentDefinition(fieldId);
  const actualFieldType = fieldType || fieldDef?.type || 'text';
  
  // State for common field configurations
  const [fieldLabel, setFieldLabel] = useState('');
  const [exclusionType, setExclusionType] = useState<'hide' | 'disable'>('hide');
  const [showTitle, setShowTitle] = useState(true);
  const [displayName, setDisplayName] = useState('');
  
  // State for MultiChoice specific configurations
  const [style, setStyle] = useState<'Chip' | 'Checkbox' | 'Radio Button' | 'Dropdown'>('Chip');
  const [layoutDirection, setLayoutDirection] = useState<'Horizontal' | 'Vertical'>('Horizontal');
  const [chipSize, setChipSize] = useState<'Medium' | 'Large'>('Medium');
  const [showImage, setShowImage] = useState(false);
  const [imageDisplay, setImageDisplay] = useState<'Fit' | 'Fill' | 'Stretch'>('Fit');
  const [layoutStyle, setLayoutStyle] = useState<'Standard' | 'Padded Frame'>('Standard');
  const [captionAlignment, setCaptionAlignment] = useState<'Left' | 'Centre' | 'Right'>('Centre');
  const [columnSelection, setColumnSelection] = useState<'Auto' | '1' | '2' | '3' | '4'>('Auto');
  const [fillDirection, setFillDirection] = useState<'Horizontal' | 'Vertical'>('Vertical');
  const [chipWidth, setChipWidth] = useState<'Fixed' | 'Dynamic'>('Fixed');
  const [chipLabelPosition, setChipLabelPosition] = useState<'Right side of image' | 'Below Image' | 'Show only on hover' | 'Hide label'>('Below Image');
  const [showSearchBar, setShowSearchBar] = useState(false);
  const [showExpandCollapse, setShowExpandCollapse] = useState(false);
  
  const [loadOption, setLoadOption] = useState<'All' | 'Partial'>('All');
  const [loadMoreButtonLabel, setLoadMoreButtonLabel] = useState('Show More');
  const [loadLessButtonLabel, setLoadLessButtonLabel] = useState('Show Less');
  const [initialLoadCount, setInitialLoadCount] = useState<number>(5);
  const [loadRemainingOption, setLoadRemainingOption] = useState<'Show All' | 'In Batch'>('Show All');
  const [batchCount, setBatchCount] = useState<number>(5);
  
  const [excludedOption, setExcludedOption] = useState<'Hide' | 'Disable' | 'Disable and moved to the bottom of the list'>('Disable');
  
  const findFieldConfiguration = (containers: ContainerType[], fieldId: string): any => {
    for (const container of containers) {
      const field = container.content.items?.find(item => 
        item.type === 'field' && item.id === fieldId
      );
      
      if (field) {
        return field.configuration || {};
      }
      
      if (container.content.nestedContainers && container.content.nestedContainers.length > 0) {
        const nestedConfig = findFieldConfiguration(container.content.nestedContainers, fieldId);
        if (nestedConfig) {
          return nestedConfig;
        }
      }
    }
    
    return null;
  };
  
  useEffect(() => {
    if (fieldId && editorState.activePageId && containers[editorState.activePageId]) {
      const pageContainers = containers[editorState.activePageId];
      
      const fieldConfig = findFieldConfiguration(pageContainers, fieldId);
      
      if (fieldConfig) {
        setFieldLabel(fieldConfig.label || '');
        setExclusionType(fieldConfig.exclusionType || 'hide');
        setShowTitle(fieldConfig.showTitle !== undefined ? fieldConfig.showTitle : true);
        setDisplayName(fieldConfig.displayName || '');
        
        if (actualFieldType === 'multichoice') {
          setStyle(fieldConfig.style || 'Chip');
          setLayoutDirection(fieldConfig.layoutDirection || 'Horizontal');
          setChipSize(fieldConfig.chipSize || 'Medium');
          setShowImage(fieldConfig.showImage || false);
          setImageDisplay(fieldConfig.imageDisplay || 'Fit');
          setLayoutStyle(fieldConfig.layoutStyle || 'Standard');
          setCaptionAlignment(fieldConfig.captionAlignment || 'Centre');
          setColumnSelection(fieldConfig.columnSelection || 'Auto');
          setFillDirection(fieldConfig.fillDirection || 'Vertical');
          setChipWidth(fieldConfig.chipWidth || 'Fixed');
          setChipLabelPosition(fieldConfig.chipLabelPosition || 'Below Image');
          setShowSearchBar(fieldConfig.showSearchBar || false);
          setShowExpandCollapse(fieldConfig.showExpandCollapse || false);
          // Load Options
          setLoadOption(fieldConfig.loadOption || 'All');
          setLoadMoreButtonLabel(fieldConfig.loadMoreButtonLabel || 'Show More');
          setLoadLessButtonLabel(fieldConfig.loadLessButtonLabel || 'Show Less');
          setInitialLoadCount(fieldConfig.initialLoadCount || 5);
          setLoadRemainingOption(fieldConfig.loadRemainingOption || 'Show All');
          setBatchCount(fieldConfig.batchCount || 5);
          // Excluded options
          setExcludedOption(fieldConfig.excludedOption || 'Disable');
        }
      } else if (fieldDef) {
        setFieldLabel(fieldDef.name || '');
      }
    }
  }, [fieldId, actualFieldType, editorState.activePageId, containers, fieldDef]);
  
  // Update handler
  const handleConfigUpdate = (key: string, value: any) => {
    // First update the state
    switch (key) {
      case 'label':
        setFieldLabel(value);
        break;
      case 'exclusionType':
        setExclusionType(value);
        break;
      case 'showTitle':
        setShowTitle(value);
        break;
      case 'displayName':
        setDisplayName(value);
        break;
      case 'style':
        setStyle(value);
        break;
      case 'layoutDirection':
        setLayoutDirection(value);
        break;
      case 'chipSize':
        setChipSize(value);
        break;
      case 'showImage':
        setShowImage(value);
        break;
      case 'imageDisplay':
        setImageDisplay(value);
        break;
      case 'layoutStyle':
        setLayoutStyle(value);
        break;
      case 'captionAlignment':
        setCaptionAlignment(value);
        break;
      case 'columnSelection':
        setColumnSelection(value);
        break;
      case 'fillDirection':
        setFillDirection(value);
        break;
      case 'chipWidth':
        setChipWidth(value);
        break;
      case 'chipLabelPosition':
        console.log('FieldConfigPanel - chipLabelPosition change:', value);
        setChipLabelPosition(value);
        break;
      case 'showSearchBar':
        setShowSearchBar(value);
        break;
      case 'showExpandCollapse':
        setShowExpandCollapse(value);
        break;
      case 'loadOption':
        setLoadOption(value);
        break;
      case 'loadMoreButtonLabel':
        setLoadMoreButtonLabel(value);
        break;
      case 'loadLessButtonLabel':
        setLoadLessButtonLabel(value);
        break;
      case 'initialLoadCount':
        setInitialLoadCount(value);
        break;
      case 'loadRemainingOption':
        setLoadRemainingOption(value);
        break;
      case 'batchCount':
        console.log('FieldConfigPanel - batchCount update:', value);
        setBatchCount(value);
        break;
      case 'excludedOption':
        setExcludedOption(value);
        break;
    }

    // Build config with updated values
    const updatedConfig = {
      id: fieldId,
      label: key === 'label' ? value : fieldLabel,
      exclusionType: key === 'exclusionType' ? value : exclusionType,
      showTitle: key === 'showTitle' ? value : showTitle,
      displayName: key === 'displayName' ? value : displayName,
      ...(actualFieldType === 'multichoice' ? {
        style: key === 'style' ? value : style,
        layoutDirection: key === 'layoutDirection' ? value : layoutDirection,
        chipSize: key === 'chipSize' ? value : chipSize,
        showImage: key === 'showImage' ? value : showImage,
        imageDisplay: key === 'imageDisplay' ? value : imageDisplay,
        layoutStyle: key === 'layoutStyle' ? value : layoutStyle,
        captionAlignment: key === 'captionAlignment' ? value : captionAlignment,
        columnSelection: key === 'columnSelection' ? value : columnSelection,
        fillDirection: key === 'fillDirection' ? value : fillDirection,
        chipWidth: key === 'chipWidth' ? value : chipWidth,
        chipLabelPosition: key === 'chipLabelPosition' ? value : chipLabelPosition,
        showSearchBar: key === 'showSearchBar' ? value : showSearchBar,
        showExpandCollapse: key === 'showExpandCollapse' ? value : showExpandCollapse,
        // Load Options
        loadOption: key === 'loadOption' ? value : loadOption,
        loadMoreButtonLabel: key === 'loadMoreButtonLabel' ? value : loadMoreButtonLabel,
        loadLessButtonLabel: key === 'loadLessButtonLabel' ? value : loadLessButtonLabel,
        initialLoadCount: key === 'initialLoadCount' ? value : initialLoadCount,
        loadRemainingOption: key === 'loadRemainingOption' ? value : loadRemainingOption,
        batchCount: key === 'batchCount' ? value : batchCount,
        excludedOption: key === 'excludedOption' ? value : excludedOption,
      } : {})
    };

    console.log('FieldConfigPanel - Built config:', { key, value, chipLabelPosition: updatedConfig.chipLabelPosition });

    if (onConfigChange) {
      onConfigChange(updatedConfig);
    }
  };
  
  const renderFieldConfig = () => {
    switch (actualFieldType) {
      case 'text':
        return (
          <TextFieldConfig
            label={fieldLabel}
            onLabelChange={(value) => handleConfigUpdate('label', value)}
            exclusionType={exclusionType}
            onExclusionChange={(value) => handleConfigUpdate('exclusionType', value)}
            showTitle={showTitle}
            onShowTitleChange={(value) => handleConfigUpdate('showTitle', value)}
            displayName={displayName}
            onDisplayNameChange={(value) => handleConfigUpdate('displayName', value)}
          />
        );
        
      case 'integer':
        return (
          <IntegerFieldConfig
            label={fieldLabel}
            onLabelChange={(value) => handleConfigUpdate('label', value)}
            exclusionType={exclusionType}
            onExclusionChange={(value) => handleConfigUpdate('exclusionType', value)}
            showTitle={showTitle}
            onShowTitleChange={(value) => handleConfigUpdate('showTitle', value)}
            displayName={displayName}
            onDisplayNameChange={(value) => handleConfigUpdate('displayName', value)}
          />
        );
        
      case 'decimal':
        return (
          <DecimalFieldConfig
            label={fieldLabel}
            onLabelChange={(value) => handleConfigUpdate('label', value)}
            exclusionType={exclusionType}
            onExclusionChange={(value) => handleConfigUpdate('exclusionType', value)}
            showTitle={showTitle}
            onShowTitleChange={(value) => handleConfigUpdate('showTitle', value)}
            displayName={displayName}
            onDisplayNameChange={(value) => handleConfigUpdate('displayName', value)}
          />
        );
        
      case 'fraction':
        return (
          <FractionFieldConfig
            label={fieldLabel}
            onLabelChange={(value) => handleConfigUpdate('label', value)}
            exclusionType={exclusionType}
            onExclusionChange={(value) => handleConfigUpdate('exclusionType', value)}
            showTitle={showTitle}
            onShowTitleChange={(value) => handleConfigUpdate('showTitle', value)}
            displayName={displayName}
            onDisplayNameChange={(value) => handleConfigUpdate('displayName', value)}
          />
        );
        
      case 'range':
        return (
          <RangeFieldConfig
            label={fieldLabel}
            onLabelChange={(value) => handleConfigUpdate('label', value)}
            exclusionType={exclusionType}
            onExclusionChange={(value) => handleConfigUpdate('exclusionType', value)}
            showTitle={showTitle}
            onShowTitleChange={(value) => handleConfigUpdate('showTitle', value)}
            displayName={displayName}
            onDisplayNameChange={(value) => handleConfigUpdate('displayName', value)}
          />
        );
        
      case 'multichoice':
        return (
          <MultiChoiceFieldConfig
            fieldName={fieldDef?.name || 'MultiChoice Field'}
            label={fieldLabel}
            onLabelChange={(value) => handleConfigUpdate('label', value)}
            exclusionType={exclusionType}
            onExclusionChange={(value) => handleConfigUpdate('exclusionType', value)}
            showTitle={showTitle}
            onShowTitleChange={(value) => handleConfigUpdate('showTitle', value)}
            displayName={displayName}
            onDisplayNameChange={(value) => handleConfigUpdate('displayName', value)}
            style={style}
            onStyleChange={(value) => handleConfigUpdate('style', value)}
            chipSize={chipSize}
            onChipSizeChange={(value) => handleConfigUpdate('chipSize', value)}
            showImage={showImage}
            onShowImageChange={(value) => handleConfigUpdate('showImage', value)}
            imageDisplay={imageDisplay}
            onImageDisplayChange={(value) => handleConfigUpdate('imageDisplay', value)}
            layoutStyle={layoutStyle}
            onLayoutStyleChange={(value) => handleConfigUpdate('layoutStyle', value)}
            captionAlignment={captionAlignment}
            onCaptionAlignmentChange={(value) => handleConfigUpdate('captionAlignment', value)}
            columnSelection={columnSelection}
            onColumnSelectionChange={(value) => handleConfigUpdate('columnSelection', value)}
            fillDirection={fillDirection}
            onFillDirectionChange={(value) => handleConfigUpdate('fillDirection', value)}
            layoutDirection={layoutDirection}
            onLayoutDirectionChange={(value) => handleConfigUpdate('layoutDirection', value)}
            chipWidth={chipWidth}
            onChipWidthChange={(value) => handleConfigUpdate('chipWidth', value)}
            chipLabelPosition={chipLabelPosition}
            onChipLabelPositionChange={(value) => handleConfigUpdate('chipLabelPosition', value)}
            showSearchBar={showSearchBar}
            onShowSearchBarChange={(value) => handleConfigUpdate('showSearchBar', value)}
            showExpandCollapse={showExpandCollapse}
            onShowExpandCollapseChange={(value) => handleConfigUpdate('showExpandCollapse', value)}
            // Load Options props
            loadOption={loadOption}
            onLoadOptionChange={(value) => handleConfigUpdate('loadOption', value)}
            loadMoreButtonLabel={loadMoreButtonLabel}
            loadLessButtonLabel={loadLessButtonLabel}
            onLoadMoreButtonLabelChange={(value) => handleConfigUpdate('loadMoreButtonLabel', value)}
            onLoadLessButtonLabelChange={(value) => handleConfigUpdate('loadLessButtonLabel', value)}
            initialLoadCount={initialLoadCount}
            onInitialLoadCountChange={(value) => handleConfigUpdate('initialLoadCount', value)}
            loadRemainingOption={loadRemainingOption}
            onLoadRemainingOptionChange={(value) => handleConfigUpdate('loadRemainingOption', value)}
            batchCount={batchCount}
            onBatchCountChange={(value) => handleConfigUpdate('batchCount', value)}
            // Excluded options props
            excludedOption={excludedOption}
            onExcludedOptionChange={(value) => handleConfigUpdate('excludedOption', value)}
          />
        );
        case 'boolean':
      return (
        <BooleanFieldConfig
          label={fieldLabel}
          onLabelChange={(value) => handleConfigUpdate('label', value)}
          exclusionType={exclusionType}
          onExclusionChange={(value) => handleConfigUpdate('exclusionType', value)}
          showTitle={showTitle}
          onShowTitleChange={(value) => handleConfigUpdate('showTitle', value)}
          displayName={displayName}
          onDisplayNameChange={(value) => handleConfigUpdate('displayName', value)}
        />
      );
      
    case 'connector':
      return (
        <ConnectorConfig
          label={fieldLabel}
          onLabelChange={(value) => handleConfigUpdate('label', value)}
          exclusionType={exclusionType}
          onExclusionChange={(value) => handleConfigUpdate('exclusionType', value)}
          showTitle={showTitle}
          onShowTitleChange={(value) => handleConfigUpdate('showTitle', value)}
          displayName={displayName}
          onDisplayNameChange={(value) => handleConfigUpdate('displayName', value)}
        />
      );
        
      default:
        return (
          <TextFieldConfig
            label={fieldLabel}
            onLabelChange={(value) => handleConfigUpdate('label', value)}
            exclusionType={exclusionType}
            onExclusionChange={(value) => handleConfigUpdate('exclusionType', value)}
            showTitle={showTitle}
            onShowTitleChange={(value) => handleConfigUpdate('showTitle', value)}
            displayName={displayName}
            onDisplayNameChange={(value) => handleConfigUpdate('displayName', value)}
          />
        );
    }
  };
  
  return (
    <div className={styles.container}>
      <div className="tw-flex tw-justify-between tw-items-center tw-px-4 tw-py-2 tw-border-b tw-border-[#D1D1D6] tw-mb-4">
        <div className="tw-flex tw-items-center tw-gap-2">
          <MultiChoiceIcon />
          <h2 className="tw-text-[#6D788C] tw-text-sm tw-font-semibold">
            Field
          </h2>
        </div>
        <button className="tw-rounded tw-w-6 tw-h-6 tw-flex tw-items-center tw-justify-center">
          <OverFlowIcon />
        </button>
      </div>
      
      <div className="tw-px-4 tw-py-1">
        {renderFieldConfig()}
      </div>
    </div>
  );
};